import React, { useState } from 'react';
import { Popover, Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { stripHtmlTags } from '../utils/renderHtml';

interface ExpandableCellProps {
  content: React.ReactNode;
  isHtml?: boolean;
  maxLength?: number;
}

/**
 * Cell renderer with "Show More" button and popover for long content
 * Used in DataGrid cells to handle expandable content
 */
const ExpandableCell: React.FC<ExpandableCellProps> = ({
  content,
  isHtml = false,
  maxLength = 100,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Convert content to string for length check
  const contentString =
    typeof content === 'string'
      ? isHtml
        ? stripHtmlTags(content)
        : content
      : String(content);

  const isTruncated = contentString.length > maxLength;
  const displayContent = isTruncated
    ? contentString.slice(0, maxLength) + '...'
    : contentString;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={styles.expandableCellContainer}>
      <Typography variant="body2" sx={styles.expandableCellText}>
        {isHtml ? (
          <span dangerouslySetInnerHTML={{ __html: displayContent }} />
        ) : (
          displayContent
        )}
      </Typography>

      {isTruncated && (
        <>
          <Box component="span" onClick={handleClick} sx={styles.showMoreText}>
            more
          </Box>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}>
            <Box sx={styles.popoverContent}>
              {isHtml ? (
                <div dangerouslySetInnerHTML={{ __html: content as string }} />
              ) : (
                <Typography variant="body2">{content}</Typography>
              )}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

interface ExpandableListCellProps {
  items: string[];
  maxItems?: number;
  isHtml?: boolean;
}

/**
 * Cell renderer for list items with expand/collapse functionality
 * Shows first N items with "Show More" to see the rest
 */
export const ExpandableListCell: React.FC<ExpandableListCellProps> = ({
  items,
  maxItems = 2,
  isHtml = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!items || items.length === 0) {
    return <Typography variant="body2">-</Typography>;
  }

  const isTruncated = items.length > maxItems;
  const displayItems = isTruncated ? items.slice(0, maxItems) : items;
  const remainingCount = items.length - maxItems;

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={styles.expandableListContainer}>
      {displayItems.map((item, index) => (
        <Typography
          key={index}
          variant="body2"
          component="span"
          sx={styles.listItemText}>
          {isHtml ? (
            <span dangerouslySetInnerHTML={{ __html: stripHtmlTags(item) }} />
          ) : (
            item
          )}
          {index < displayItems.length - 1 && ','}
        </Typography>
      ))}

      {isTruncated && (
        <>
          <span
            onClick={handleClick}
            title="Click to view all"
            style={{
              fontSize: '0.75rem',
              color: '#1976d2',
              cursor: 'pointer',
              userSelect: 'none',
              padding: '2px 4px',
              display: 'inline-block',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.textDecoration = 'underline';
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.textDecoration = 'none';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}>
            +{remainingCount} more
          </span>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: { maxWidth: 400, maxHeight: 400 },
              },
            }}>
            <Box sx={styles.listPopoverContent}>
              {items.map((item, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={styles.listPopoverItem}>
                  {isHtml ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: stripHtmlTags(item) }}
                    />
                  ) : (
                    item
                  )}
                </Typography>
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

const styles: Record<string, SxProps<Theme>> = {
  expandableCellContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  expandableCellText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  showMoreText: {
    minWidth: 'auto',
    fontSize: '0.75rem',
    color: 'primary.main',
    cursor: 'pointer',
    flexShrink: 0,
    userSelect: 'none',
    padding: '2px 4px',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'action.hover',
    },
  },
  popoverContent: {
    p: 2,
    maxWidth: 600,
    maxHeight: 400,
    overflow: 'auto',
  },
  expandableListContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0.25,
    width: '100%',
    flexWrap: 'wrap',
    lineHeight: 1.2,
  },
  listItemText: {
    display: 'inline',
    lineHeight: 1.2,
  },
  listShowMoreText: {
    alignSelf: 'flex-start',
    minWidth: 'auto',
    fontSize: '0.75rem',
    color: 'primary.main',
    cursor: 'pointer',
    userSelect: 'none',
    padding: '2px 4px',
    display: 'inline-block',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'action.hover',
    },
  },
  listPopoverContent: {
    p: 2,
    maxWidth: 400,
    maxHeight: 400,
    overflow: 'auto',
  },
  listPopoverItem: {
    mb: 0.5,
  },
};

export default ExpandableCell;
